import each from 'lodash/each';
import clone from 'lodash/clone';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import map from 'lodash/map';

import logger from '../../utils/logger';
import config from 'config';

class Facebook {
  _init(state, commit, id) {
    if (!state.currentIds.facebook.includes(id)) {
      fbq('init', id);

      commit('trackId', { property: 'facebook', value: id });

      logger.log(`${id} initialize.`);
    } else {
      logger.log(`${id} already initialized.`);
    }
  }

  _clientInit(state, commit, info, payload, type) {
    this._init(state, commit, info.id);

    this._customVars(info.custom_vars, payload, type);
  }

  _customVars(customVars, payload, type) {
    if (customVars[type]) {
      each(customVars[type], customVar => {
        payload[customVar.key] = customVar.value;
      });
    }
  }

  _trackEvent(id, fbEvent, eventType, payload, payloadType) {
    if (payload) {
      fbq('trackSingle', id, fbEvent, payload);

      logger.log(`${payloadType ? `${payloadType} ` : ''}${id} ${eventType} event with payload:`);

      logger.log(payload);
    } else {
      fbq('trackSingle', id, fbEvent);

      logger.log(`${payloadType ? `${payloadType} ` : ''}${id} ${eventType} event`);
    }
  }

  _acquisitionId(rootState) {
    return rootState.isWhitelabel ? rootState.domainConfig.meta.fb_pixel_id : config.FB_ACQN_ID;
  }

  _id(rootState) {
    return rootState.isWhitelabel ? rootState.domainConfig.meta.fb_pixel_id : config.FB_ID;
  }

  _groupCollapsed(rootState) {
    logger.groupCollapsed(`${rootState.isWhitelabel ? 'WL' : 'VS'} Facebook`);
  }

  _content(rootState, state) {
    return {
      content_ids: [rootState.isWhitelabel ? state.campaign.slug : state.campaign.analytics_cid],
      content_type: rootState.isWhitelabel ? 'product_group' : 'product',
    };
  }

  viewPage({ rootState, state, commit }) {
    let acquisitionId = this._acquisitionId(rootState),
      viewContentPayload,
      homeOrProducts = ['home', 'products'],
      onHomeOrProductsPage = homeOrProducts.includes(rootState.route.name) && (rootState.isWhitelabel && rootState.home.campaigns.collection.length),
      onCollectionPage = rootState.route.name === 'collection' && rootState.collection.campaigns.length;

    if (rootState.isWhitelabel && (onHomeOrProductsPage || onCollectionPage)) {
      viewContentPayload = {
        content_type: 'product_group',
      };

      if (onHomeOrProductsPage) {
        viewContentPayload.content_ids = map(rootState.home.campaigns.collection, campaign => campaign.uri);
      } else {
        viewContentPayload.content_ids = map(rootState.collection.campaigns, campaign => campaign.uri.split('/')[2]);
      }
    }

    this._groupCollapsed(rootState);


    if (acquisitionId) {
      this._init(state, commit, acquisitionId);

      this._trackEvent(acquisitionId, 'PageView', 'page view');

      this._trackEvent(acquisitionId, 'ViewContent', 'view content', viewContentPayload);
    } else {
      logger.log('WL Facebook not configured.');
    }

    logger.groupEnd();
  }

  vsViewSales({ rootState, state, commit }) {
    let id = this._id(rootState);

    this._groupCollapsed(rootState);

    if (logger.checkRetargeting(state.campaign.tracking.global.disable_vs_retargeting) || rootState.isWhitelabel) {
      if (id) {
        this._init(state, commit, id);

        let pageView = {
          ...this._content(rootState, state),
          content_category: state.campaign.category,
          // duplicate because I was sending the wrong one before and I do not want to throw off anyones analytics
          product_category: state.campaign.category,
          content_name: state.campaign.name,
        },
          viewContent = Object.assign({
            campaign_url: state.campaign.url,
            currency: 'USD',
            value: parseFloat(state.product.price),
          }, pageView);

        this._trackEvent(id, 'PageView', 'page view', pageView);

        this._trackEvent(id, 'ViewContent', 'view content', viewContent);
      } else {
        logger.log('WL Facebook not configured.');
      }
    } else {
      logger.log('VS tracking disabled.')
    }

    logger.groupEnd();
  }

  vsViewThankYou({ rootState, state, commit }) {
    let id = this._id(rootState);

    this._groupCollapsed(rootState);

    let thankYou = {
      contents: [],
      content_type: rootState.isWhitelabel ? 'product_group' : 'product',
      currency: 'USD',
      value: 0,
      num_items: 0,
    };

    each(state.order.orders, order => {
      if (logger.checkRetargeting(state.order.tracking[order.analytics_cid].global.disable_vs_retargeting) || rootState.isWhitelabel) {
        each(order.products, item => {
          thankYou.contents.push({
            id: `${rootState.isWhitelabel ? order.campaign_slug : order.analytics_cid}`,
            quantity: item.quantity,
            item_price: Math.round(item.discounted_price / item.quantity * 100) / 100,
            category: order.campaign_category,
            name: order.campaign_name,
            type: item.type,
            color: item.color,
            size: item.size,
          });

          thankYou.value += item.discounted_price;

          thankYou.num_items += item.quantity;
        });
      }
    });

    thankYou.value = Math.round(thankYou.value * 100) / 100;

    if (id && thankYou.contents.length) {
      this._init(state, commit, id);

      this._trackEvent(id, 'PageView', 'page view', thankYou);

      this._trackEvent(id, 'Purchase', 'purchase', thankYou);
    } else if (rootState.isWhitelabel) {
      logger.log('VS tracking disabled.')
    }

    logger.groupEnd();
  }

  vsAddToCart({ rootState, state, commit }) {
    let id = this._id(rootState);

    this._groupCollapsed(rootState);

    if (logger.checkRetargeting(state.campaign.tracking.global.disable_vs_retargeting) || rootState.isWhitelabel) {
      if (id) {
        this._init(state, commit, id);

        let addToCart = {
          ...this._content(rootState, state),
          content_category: state.campaign.category,
          // duplicate because I was sending the wrong one before and I do not want to throw off anyones analytics
          product_category: state.campaign.category,
          content_name: state.campaign.name,
          currency: 'USD',
          value: parseFloat(state.product.price) * state.quantity,
        };

        this._trackEvent(id, 'AddToCart', 'add to cart', addToCart);
      } else {
        logger.log('WL Facebook not configured.');
      }
    } else {
      logger.log('VS tracking disabled.')
    }

    logger.groupEnd();
  }

  vsInitiateCheckout({ rootState, state, commit }) {
    let id = this._id(rootState);

    this._groupCollapsed(rootState);

    let firstItem = state.cart.items[0],
      initiateCheckout = {
        contents: [],
        content_type: rootState.isWhitelabel ? 'product_group' : 'product',
        currency: 'USD',
        value: 0,
        num_items: 0,
      };

    each(state.cart.items, item => {
      if (logger.checkRetargeting(state.cart.tracking[item.analytics_cid].global.disable_vs_retargeting) || rootState.isWhitelabel) {
        initiateCheckout.contents.push({
          id: `${rootState.isWhitelabel ? item.campaign_slug : item.analytics_cid}`,
          quantity: item.quantity,
          item_price: item.price_per_unit,
          category: item.campaign_category,
          name: item.campaign_name,
          type: item.type,
          color: item.color,
          size: item.size,
        });

        initiateCheckout.value += item.price;

        initiateCheckout.num_items += item.quantity;
      }
    });

    initiateCheckout.value = Math.round(initiateCheckout.value * 100) / 100;

    if (id && initiateCheckout.contents.length) {
      this._init(state, commit, id);

      this._trackEvent(id, 'InitiateCheckout', 'initiate checkout', initiateCheckout);
    } else if (rootState.isWhitelabel) {
      logger.log('VS tracking disabled.')
    }

    logger.groupEnd();
  }

  vsRegister() {
    logger.groupCollapsed('Client Facebook');

    this._trackEvent(config.FB_ID, 'CompleteRegistration', 'register');

    logger.groupEnd();
  }

  clientViewSales({ state, commit }) {
    logger.groupCollapsed('Client Facebook');

    let infoGlobal = state.campaign.tracking.global.facebook,
      infoCampaign = state.campaign.tracking.campaign.facebook,
      pageView = {
        content_name: state.campaign.name,
        campaign_url: state.campaign.url,
        content_category: state.campaign.category,
        // duplicate because I was sending the wrong one before and I do not want to throw off anyones analytics
        product_category: state.campaign.category,
      },
      viewContentGlobal = Object.assign({
        content_ids: [state.campaign.slug],
        content_type: infoGlobal.use_product_group ? 'product_group' : 'product',
        currency: 'USD',
        value: parseFloat(state.product.price),
      }, pageView),
      viewContentCampaign = clone(viewContentGlobal),
      trackPageView = (info, pageView, viewContent, type) => {
        this._clientInit(state, commit, info, viewContent, 'VIEW_CONTENT');

        this._trackEvent(info.id, 'PageView', 'page view', pageView, type);

        this._trackEvent(info.id, 'ViewContent', 'view content', viewContent, type);
      };

    if (logger.checkCampaign(infoCampaign.id)) {
      trackPageView(infoCampaign, pageView, viewContentCampaign, 'Campaign specific');
    }

    if (logger.checkGlobal(infoGlobal.id, infoCampaign.id)) {
      trackPageView(infoGlobal, pageView, viewContentGlobal, 'Global');
    }

    logger.groupEnd();
  }

  clientViewCollection({ rootState, state, commit }) {
    logger.groupCollapsed('Client Facebook');

    let info = rootState.collection.info,
      payload = {
        content_name: info.name,
        store_url: info.uri,
        content_type: 'product_group',
      },
      id = get(info, 'tracking.global.facebook.id');

    if (id) {
      this._trackEvent(id, 'PageView', 'page view', payload, 'Global');

      this._trackEvent(id, 'ViewContent', 'view content', payload, 'Global');
    } else {
      logger.log('Global not configured');
    }

    logger.groupEnd();
  }

  clientViewThankYou({ state, commit }) {
    logger.groupCollapsed('Client Facebook');

    each(state.order.orders, order => {
      logger.groupCollapsed(order.campaign_name);

      let infoGlobal = state.order.tracking[order.analytics_cid].global.facebook,
        infoCampaign = state.order.tracking[order.analytics_cid].campaign.facebook,
        thankYouGlobal = {
          contents: [],
          content_type: infoGlobal.use_product_group ? 'product_group' : 'product',
          currency: 'USD',
          value: 0,
          num_items: 0,
        },
        thankYouCampaign;

      each(order.products, item => {
        thankYouGlobal.contents.push({
          id: order.campaign_slug,
          quantity: item.quantity,
          item_price: Math.round(item.discounted_price / item.quantity * 100) / 100,
          category: order.campaign_category,
          name: order.campaign_name,
          type: item.type,
          color: item.color,
          size: item.size,
        });

        thankYouGlobal.value += item.discounted_price;

        thankYouGlobal.num_items += item.quantity;
      });

      thankYouGlobal.value = Math.round(thankYouGlobal.value * 100) / 100;

      if (logger.checkCampaign(infoCampaign.id)) {
        thankYouCampaign = clone(thankYouGlobal);
        this._clientInit(state, commit, infoCampaign, thankYouCampaign, 'PURCHASE');

        this._trackEvent(infoCampaign.id, 'PageView', 'page view', thankYouCampaign);

        this._trackEvent(infoCampaign.id, 'Purchase', 'purchase', thankYouCampaign);
      }

      if (logger.checkGlobal(infoGlobal.id, infoCampaign.id)) {
        this._clientInit(state, commit, infoGlobal, thankYouGlobal, 'PURCHASE');

        this._trackEvent(infoGlobal.id, 'PageView', 'page view', thankYouGlobal);

        this._trackEvent(infoGlobal.id, 'Purchase', 'purchase', thankYouGlobal);
      }

      logger.groupEnd();
    });

    logger.groupEnd();
  }

  clientAddToCart({ state, commit }) {
    logger.groupCollapsed('Client Facebook');

    let infoGlobal = state.campaign.tracking.global.facebook,
      infoCampaign = state.campaign.tracking.campaign.facebook,
      addToCartGlobal = {
        content_ids: [state.campaign.slug],
        content_type: infoGlobal.use_product_group ? 'product_group' : 'product',
        content_name: state.campaign.name,
        content_category: state.campaign.category,
        // duplicate because I was sending the wrong one before and I do not want to throw off anyones analytics
        product_category: state.campaign.category,
        campaign_url: state.campaign.url,
        currency: 'USD',
        value: parseFloat(state.product.price) * state.quantity,
      },
      addToCartCampaign = clone(addToCartGlobal),
      trackAddToCart = (info, addToCart, type) => {
        this._clientInit(state, commit, info, addToCart, 'ADD_TO_CART');

        this._trackEvent(info.id, 'AddToCart', 'add to cart', addToCart, type);
      }

    if (logger.checkCampaign(infoCampaign.id)) {
      trackAddToCart(infoCampaign, addToCartCampaign, 'Campaign specific')
    }

    if (logger.checkGlobal(infoGlobal.id, infoCampaign.id)) {
      trackAddToCart(infoGlobal, addToCartGlobal, 'Global')
    }

    logger.groupEnd();
  }

  clientInitiateCheckout({ state, commit }) {
    logger.groupCollapsed('Client Facebook');

    each(groupBy(state.cart.items, 'analytics_cid'), group => {
      let firstItem = group[0],
        infoGlobal = state.cart.tracking[firstItem.analytics_cid].global.facebook,
        infoCampaign = state.cart.tracking[firstItem.analytics_cid].campaign.facebook,
        initiateCheckoutGlobal = {
          contents: [],
          content_type: infoGlobal.use_product_group ? 'product_group' : 'product',
          currency: 'USD',
          value: 0,
          num_items: 0,
          campaign_url: `${firstItem.public_name}/${firstItem.campaign_slug}`,
          content_name: firstItem.campaign_name,
          content_category: firstItem.campaign_category,
          product_category: firstItem.campaign_category,
        },
        initiateCheckoutCampaign,
        trackInitiateCheckout = (info, initiateCheckout, type) => {
          this._clientInit(state, commit, info, initiateCheckout, 'INITIATE_CHECKOUT');

          this._trackEvent(info.id, 'InitiateCheckout', 'initiate checkout', initiateCheckout, type);
        };

      logger.groupCollapsed(firstItem.campaign_name);

      each(group, item => {
        initiateCheckoutGlobal.contents.push({
          id: item.campaign_slug,
          quantity: item.quantity,
          item_price: item.price_per_unit,
          color: item.color,
          size: item.size,
        });

        initiateCheckoutGlobal.value += item.price;

        initiateCheckoutGlobal.num_items += item.quantity;
      });

      initiateCheckoutGlobal.value = Math.round(initiateCheckoutGlobal.value * 100) / 100;

      if (logger.checkCampaign(infoCampaign.id)) {
        initiateCheckoutCampaign = clone(initiateCheckoutGlobal);

        trackInitiateCheckout(infoCampaign, initiateCheckoutCampaign, 'Campaign specific');
      }

      if (logger.checkGlobal(infoGlobal.id, infoCampaign.id)) {
        trackInitiateCheckout(infoGlobal, initiateCheckoutGlobal, 'Global');
      }

      logger.groupEnd();
    });

    logger.groupEnd();
  }
}

export default new Facebook();